import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X, Phone, Mail, ChevronDown } from 'lucide-react'

const menuItems = [
  { label: 'Forside', href: '/' },
  { label: 'Billedgalleri', href: '/galleri' },
  { label: 'Om os', href: '/om-os' },
  { label: 'Kontakt', href: '/kontakt' },
]

const serviceCategories = [
  {
    title: 'Malerarbejde',
    items: [
      { label: 'Indvendigt malerarbejde', href: '/indvendig-maling', desc: 'Professionelt malerarbejde af vægge, lofter og træværk.' },
      { label: 'Udvendigt malerarbejde', href: '/udvendig-maling', desc: 'Maling af hus og facade, træværk og vinduer m.m.' },
      { label: 'Facademaling', href: '/facademaling', desc: 'Flot og vejrbestandig maling af husets mure.' },
      { label: 'Sprøjtemaling', href: '/sproejtemaling', desc: 'Sprøjtemaling af køkkener, møbler, lofter, radiatorer m.m.' },
      { label: 'Renovering & Vedligeholdelse', href: '/renovering-og-vedligeholdelse', desc: 'Løbende vedligeholdelse og renovering.' },
    ]
  },
  {
    title: 'Klargøring & Specialer',
    items: [
      { label: 'Fuldspartling', href: '/fuldspartling', desc: 'Helt glatte og jævne vægge klar til efterbehandling.' },
      { label: 'Opsætning af filt & tapet', href: '/opsaetning-af-filt-og-tapet', desc: 'Præcis opsætning af filt, væv og tapet.' },
      { label: 'Højtryksrensning', href: '/hoejtryksrensning', desc: 'Effektiv afrensning af facader, fliser og træværk.' },
      { label: 'Forsikringsskader', href: '/forsikringsskader', desc: 'Hurtig og professionel udbedring af skader.' },
    ]
  }
]

export default function Header() {
  const [isOpen, setIsOpen] = useState(false)
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024)
      if (window.innerWidth >= 1024) {
        setIsOpen(false)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false)
  }, [location])

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(path)
  }

  return (
    <header className="sticky top-0 z-50 w-full border-b border-gray-800 bg-[#20252C] text-white backdrop-blur-md">
      {/* Main navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center gap-3 group"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          <img
            src="https://assets.auxodev.dk/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/3fcf9262-0e4a-4a97-9828-7c0f98c2c183.png"
            alt="Malerfirma Villy Poulsen Logo"
            className="h-20 w-auto object-contain"
          />
        </Link>

        {/* Desktop Menu */}
        {isDesktop ? (
          <nav className="flex items-center gap-1">
            {/* Forside */}
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg text-sm font-bold tracking-wide transition-all duration-200 ${
                isActive('/')
                  ? 'bg-[#01529E] text-white'
                  : 'text-gray-300 hover:text-white hover:bg-white/5'
              }`}
            >
              Forside
            </Link>

            {/* Vi udfører Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setIsDropdownOpen(true)}
              onMouseLeave={() => setIsDropdownOpen(false)}
            >
              <button
                className={`px-4 py-2 rounded-lg text-sm font-bold tracking-wide transition-all duration-200 flex items-center gap-1.5 focus:outline-none ${
                  isActive('/vi-udfoerer') || serviceCategories.some(cat => cat.items.some(item => isActive(item.href)))
                    ? 'bg-[#01529E] text-white'
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
                aria-expanded={isDropdownOpen}
                aria-haspopup="true"
              >
                Vi udfører
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {isDropdownOpen && (
                <div className="absolute left-1/2 -translate-x-1/2 mt-1 w-[600px] rounded-2xl bg-[#20252C] border border-gray-800 shadow-2xl p-6 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
                  <div className="grid grid-cols-2 gap-6">
                    {serviceCategories.map((category) => (
                      <div key={category.title} className="flex flex-col gap-3">
                        <h4 className="text-xs font-bold uppercase tracking-widest text-[#ED1C26] border-b border-gray-800 pb-2">
                          {category.title}
                        </h4>
                        <div className="flex flex-col gap-1">
                          {category.items.map((subItem) => (
                            <Link
                              key={subItem.href}
                              to={subItem.href}
                              onClick={() => setIsDropdownOpen(false)}
                              className={`group block p-2 rounded-xl transition-all duration-200 ${
                                location.pathname === subItem.href
                                  ? 'bg-[#01529E]/10 text-[#01529E]'
                                  : 'hover:bg-white/5 text-gray-300 hover:text-white'
                              }`}
                            >
                              <div className="text-sm font-bold leading-snug group-hover:text-[#01529E] transition-colors">
                                {subItem.label}
                              </div>
                              <div className="text-xs text-gray-400 mt-0.5 leading-normal">
                                {subItem.desc}
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 pt-4 border-t border-gray-800 flex items-center justify-between">
                    <p className="text-xs text-gray-400">
                      Malerfirma Villy Poulsen ApS &bull; Kvalitet og stabilitet i over 25 år
                    </p>
                    <Link
                      to="/vi-udfoerer"
                      onClick={() => setIsDropdownOpen(false)}
                      className="text-xs font-bold text-[#01529E] hover:text-[#01529E]/80 flex items-center gap-1 transition-colors"
                    >
                      Se alle ydelser (Oversigt) &rarr;
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Remaining Menu Items */}
            {menuItems.slice(1).map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`px-4 py-2 rounded-lg text-sm font-bold tracking-wide transition-all duration-200 ${
                  isActive(item.href)
                    ? 'bg-[#01529E] text-white'
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/kontakt"
              className="ml-4 px-5 py-2.5 rounded-lg bg-[#ED1C26] text-white text-sm font-bold shadow-md hover:bg-[#ED1C26]/90 hover:shadow-lg transition-all cursor-pointer"
            >
              Få et tilbud
            </Link>
          </nav>
        ) : (
          /* Mobile Menu Toggle */
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="p-2 rounded-lg text-gray-300 hover:text-white hover:bg-white/5 transition-colors focus:outline-none"
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        )}
      </div>

      {/* Mobile Menu Panel */}
      {!isDesktop && isOpen && (
        <div className="border-t border-gray-800 bg-[#20252C] py-4 px-4 shadow-xl animate-in slide-in-from-top duration-200 text-white max-h-[calc(100vh-80px)] overflow-y-auto">
          <nav className="flex flex-col gap-1">
            {/* Forside */}
            <Link
              to="/"
              className={`px-4 py-3 rounded-lg text-base font-bold transition-all ${
                isActive('/')
                  ? 'bg-[#01529E] text-white'
                  : 'text-gray-300 hover:text-white hover:bg-white/5'
              }`}
            >
              Forside
            </Link>

            {/* Vi udfører mobile dropdown */}
            <div className="flex flex-col">
              <button
                onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                className={`w-full text-left px-4 py-3 rounded-lg text-base font-bold flex items-center justify-between transition-all focus:outline-none ${
                  isActive('/vi-udfoerer') || serviceCategories.some(cat => cat.items.some(item => isActive(item.href)))
                    ? 'bg-[#01529E] text-white'
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
              >
                <span>Vi udfører</span>
                <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${isMobileServicesOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isMobileServicesOpen && (
                <div className="pl-4 mt-1 flex flex-col gap-4 border-l-2 border-gray-800 ml-4">
                  {serviceCategories.map((category) => (
                    <div key={category.title} className="flex flex-col gap-1">
                      <span className="text-xs font-bold uppercase tracking-wider text-[#ED1C26] px-4 py-1">
                        {category.title}
                      </span>
                      {category.items.map((subItem) => (
                        <Link
                          key={subItem.href}
                          to={subItem.href}
                          onClick={() => setIsOpen(false)}
                          className={`px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                            location.pathname === subItem.href
                              ? 'text-white font-bold bg-[#01529E]/20'
                              : 'text-gray-400 hover:text-white'
                          }`}
                        >
                          {subItem.label}
                        </Link>
                      ))}
                    </div>
                  ))}
                  <div className="border-t border-gray-800/50 pt-2">
                    <Link
                      to="/vi-udfoerer"
                      onClick={() => setIsOpen(false)}
                      className="px-4 py-2 block rounded-lg text-sm font-bold text-[#01529E] hover:text-[#01529E]/80"
                    >
                      Se alle ydelser (Oversigt) &rarr;
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Remaining Menu Items */}
            {menuItems.slice(1).map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`px-4 py-3 rounded-lg text-base font-bold transition-all ${
                  isActive(item.href)
                    ? 'bg-[#01529E] text-white'
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
              >
                {item.label}
              </Link>
            ))}
            <div className="mt-4 pt-4 border-t border-gray-800 flex flex-col gap-3">
              <a
                href="tel:40637670"
                className="flex items-center justify-center gap-2 py-3 rounded-lg bg-white text-gray-900 text-sm font-bold hover:bg-gray-100 transition-colors"
              >
                Ring: 40 63 76 70
              </a>
              <Link
                to="/kontakt"
                className="flex items-center justify-center gap-2 py-3 rounded-lg bg-[#01529E] text-white text-sm font-bold hover:bg-[#01529E]/90 transition-colors"
              >
                Få et uforpligtende tilbud
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  )
}
